/* ContractsTab — list of contracts inside the client folder.
 *
 * Cards open /smlouvy/{id} detail; pencil icon opens editor (passed via onEdit).
 * Extracted from ClientDetailRaynet.js v4.35.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ClipboardList, Plus, Pencil, FileText } from "lucide-react";
import { CONTRACT_TYPE_COLOR, barvaTextuTypu } from "@/lib/contractMeta";
import { textChybySmlouvy } from "@/lib/chybaSmlouvy";
import { ikonaProduktu } from "@/lib/ikony/ikonyProduktu";
import { openCancellationLetter, isInsuranceType } from "@/lib/cancellationLetter";
import { navigateOverlaySafe } from "@/lib/paletteNav";
import { useTextySmluv, popisekTypu } from "@/lib/smlouvy/textySmluv";

/* JAZYK × MĚNA × ZÁKON (4. 10. 2026, majitel „ano“): texty karty jdou přes
 * t() v pěti jazycích, částky v měně ZEMĚ TENANTA (CZ → Kč, SK → €, nikdy
 * natvrdo „Kč“ ani cs-CZ) a výpověď podle práva té země (CZ § 2807 NOZ,
 * SK § 800 OZ). Podrobně lib/smlouvy/textySmluv.js. */

export default function ContractsTab({ contracts, contactId: _contactId, onAdd, onEdit }) {
  const navigate = useNavigate();
  const tx = useTextySmluv();
  const { t } = tx;
  if (!contracts || contracts.length === 0) {
    return (
      <div
        className="rounded-2xl p-12 text-center"
        style={{ background: "var(--surface)", outline: "1px dashed var(--line)", outlineOffset: "-1px" }}
      >
        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center" style={{ background: "var(--accent-soft)", outline: "1px solid var(--accent-soft)", outlineOffset: "-1px" }}>
          <ClipboardList size={20} style={{ color: "var(--accent)" }} />
        </div>
        <div className="font-display text-[15px] font-semibold" style={{ color: "var(--ink)" }}>{t("smlouvaKarta.prazdnoNadpis")}</div>
        <div className="text-[12px] mt-1.5 mb-4" style={{ color: "var(--muted)" }}>
          {t("smlouvaKarta.prazdnoText")}
        </div>
        <button
          onClick={onAdd}
          data-testid="contract-add-empty"
          className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:translate-y-[-1px]"
          style={{ color: "var(--on-accent)", background: "var(--accent)", boxShadow: "0 4px 14px var(--accent-soft), inset 0 1px 0 rgba(255,255,255,0.16)" }}
        >
          <Plus size={14} /> {t("smlouvaKarta.pridat")}
        </button>
      </div>
    );
  }
  return (
    <div data-testid="contracts-list">
      <div className="flex items-center justify-between mb-3">
        <div className="text-popisek uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--muted)" }}>
          {t.plural("smlouvaKarta.pocet", contracts.length)}
        </div>
        <button
          onClick={onAdd}
          data-testid="contract-add-btn"
          className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:translate-y-[-1px]"
          style={{ color: "var(--on-accent)", background: "var(--accent)", boxShadow: "0 4px 14px var(--accent-soft), inset 0 1px 0 rgba(255,255,255,0.16)" }}
        >
          <Plus size={13} /> {t("smlouvaKarta.pridat")}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {contracts.map((ct, i) => (
          <ContractCard key={ct.id} ct={ct} i={i} tx={tx} onOpen={() => navigateOverlaySafe(navigate, `/smlouvy/${ct.id}`)} onEdit={() => onEdit?.(ct)} />
        ))}
      </div>
    </div>
  );
}

function ContractCard({ ct, i, tx, onOpen, onEdit }) {
  const { t, lang, zeme, mena, cislo, castka, datum } = tx;
  const color = CONTRACT_TYPE_COLOR[ct.type] || "#8E8E93";
  /* Písmo v barvě typu = barva smíchaná s --ink (≥ 4,5 : 1), viz contractMeta. */
  const barvaTextu = barvaTextuTypu(ct.type);
  /* Ikona produktu ze slovníku (stejná jako v Radaru příležitostí). Barva =
     barva typu smíchaná s --ink: samotná žlutá/oranžová z palety kalendáře
     nemá na světlé ploše ani 3 : 1. */
  const Ikona = ikonaProduktu(ct.type).ikona;
  /* Částky v měně tenanta (CZ → Kč, SK → €), oddělovače podle jazyka.
     Dřív natvrdo toLocaleString("cs-CZ") + „Kč“ — slovenský poradce viděl
     své eura podepsané jako koruny. Přepočet se nedělá (lib/tenantMena.js). */
  const monthly = ct.monthly_amount ? cislo(ct.monthly_amount) : null;
  const total = ct.total_amount ? castka(ct.total_amount) : null;
  /* Průběžný stav — aby karta ve složce a detail smlouvy neříkaly každý něco
   * jiného. Dřív karta ukázala jen Celkem 3 000 000 Kč a detail k tomu vedle
   * dopočítal Zbývá splatit 2 400 000 Kč (80 % odhad z kódu) — poradce netušil,
   * které číslo platí. Ukazuje se jen to, co je opravdu vyplněné. */
  const prubezne = [
    { label: t("smlouvaKarta.zbyvaSplatit"), v: ct.remaining_amount },
    { label: t("smlouvaKarta.nasporeno"), v: ct.saved_amount },
    { label: t("smlouvaKarta.aktualniHodnota"), v: ct.current_value },
  ].find((x) => x.v !== null && x.v !== undefined && x.v !== "");
  /* Smlouva přenesená z analýzy (měl ji před analýzou). Primárně podle
     details.analyza_klic (zapisuje propis), poznámka jen jako záloha pro stará
     data a starý backend (velká kontrola 5. 10. 2026, T1-16). */
  const zAnalyzy = !!(ct.details && ct.details.analyza_klic) || /z anal[ýy]zy/i.test(ct.notes || "");
  const [generating, setGenerating] = useState(false);
  const vypoved = async (e) => {
    e.stopPropagation();
    setGenerating(true);
    try {
      /* Výpověď podle země tenanta. Starý backend vrátí český vzor i slovenskému
         poradci — pak varování místo „hotovo“ (okno to řekne taky). */
      /* Datum ukončení = to, co backend dosadí do dopisu (výročí, jinak konec).
         Okno podle něj upozorní, když už minulo. */
      const vysledek = await openCancellationLetter(ct.id, { lang, zeme, datumUkonceni: ct.anniversary_date || ct.end_date || null });
      if (vysledek?.nesoulad) toast.warning(t("smlouvaKarta.toastStaryServer"));
      else toast.success(t("smlouvaKarta.toastHotovo"));
    }
    catch (err) { toast.error(err?.message === "popup-blocked" ? t("smlouvaKarta.toastPopup") : textChybySmlouvy(err, t("smlouvaKarta.toastChyba"), lang)); }
    finally { setGenerating(false); }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ delay: i * 0.04, type: "spring", stiffness: 360, damping: 28 }}
      onClick={onOpen}
      className="elite-card relative overflow-hidden p-4 cursor-pointer group"
      data-testid={`contract-card-${ct.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-1 right-1 min-w-[44px] min-h-[44px] flex items-center justify-center opacity-40 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity rounded-lg hover:bg-black/5"
        style={{ color: "var(--muted)" }}
        data-testid={`contract-edit-${ct.id}`}
        aria-label={t("smlouvaKarta.upravit")}
      >
        <Pencil size={13} />
      </button>
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)` }} />
      <div className="flex items-start gap-3 pl-2">
        <div
          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${color}14`, outline: `1px solid ${color}33`, outlineOffset: "-1px", color: `color-mix(in oklab, ${color} 50%, var(--ink))` }}
          data-testid={`contract-ikona-${ct.id}`}
        >
          <Ikona size={18} strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          {/* pr-10: vpravo nahoře leží tužka 44 × 44 px (absolute). Bez místa
              se do ní ve vietnamštině na 390 px zasouval štítek pojišťovny
              a ťuknutí na jeho konec otevřelo editaci (ověření 4. 10. 2026). */}
          <div className="flex items-baseline gap-2 flex-wrap pr-10" data-testid={`contract-hlavicka-${ct.id}`}>
            <span className="font-display text-[15px] font-semibold tracking-tight" style={{ color: "var(--ink)" }}>
              {popisekTypu(t, ct.type)}
            </span>
            {ct.provider && (
              <span
                className="text-popisek uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
                style={{ background: `${color}14`, color: barvaTextu, outline: `1px solid ${color}33`, outlineOffset: "-1px" }}
              >
                {ct.provider}
              </span>
            )}
            {zAnalyzy && (
              <span
                className="text-drobny px-1.5 py-0.5 rounded font-semibold"
                style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
                title={t("smlouvaKarta.predAnalyzouTitle")}
                data-testid={`contract-z-analyzy-${ct.id}`}
              >
                {t("smlouvaKarta.predAnalyzou")}
              </span>
            )}
          </div>
          {/* Číslo smlouvy se NEVYMÝŠLÍ. Analýza dřív do tohohle pole psala popisek
              řádku (Pojištění auta) a ve složce pak seděla smlouva s číslem, které
              klient nikdy neviděl. Teď se prázdné číslo přizná nahlas — poradce
              vidí, že je co doplnit, místo aby věřil vymyšlenému číslu. */}
          {ct.contract_number ? (
            <div className="text-drobny mono mt-0.5" style={{ color: "var(--muted)" }} data-testid={`contract-cislo-${ct.id}`}>{t("smlouvaKarta.cislo", { cislo: ct.contract_number })}</div>
          ) : (
            <div className="text-drobny mt-0.5 italic" style={{ color: "var(--muted)" }} data-testid={`contract-bez-cisla-${ct.id}`}>{t("smlouvaKarta.bezCisla")}</div>
          )}
          {/* Popisek řádku z analýzy — informace, kterou dřív polykalo pole s číslem. */}
          {ct.details?.analyza_oznaceni && (
            <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }} data-testid={`contract-z-analyzy-popis-${ct.id}`}>
              {t("smlouvaKarta.zAnalyzy", { popis: ct.details.analyza_oznaceni })}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-drobny">
            {monthly && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("smlouvaKarta.mesicne")}</div>
                <div className="font-display text-[15px] font-semibold mono tabular-nums" style={{ color: barvaTextu }} data-testid={`contract-mesicne-${ct.id}`}>{monthly} <span className="text-drobny" style={{ color: "var(--muted)" }}>{mena.symbol}</span></div>
              </div>
            )}
            {total && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("smlouvaKarta.celkem")}</div>
                <div className="font-semibold mono tabular-nums" style={{ color: "var(--ink)" }} data-testid={`contract-celkem-${ct.id}`}>{total}</div>
              </div>
            )}
            {prubezne && (
              <div data-testid={`contract-prubezne-${ct.id}`}>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{prubezne.label}</div>
                <div className="font-semibold mono tabular-nums" style={{ color: "var(--ink)" }}>{castka(prubezne.v)}</div>
              </div>
            )}
            {ct.fixation_end && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("smlouvaKarta.fixaceDo")}</div>
                <div className="font-semibold mono" style={{ color: "var(--ink)" }} data-testid={`contract-fixace-${ct.id}`}>{datum(ct.fixation_end)}</div>
              </div>
            )}
            {ct.anniversary_date && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("smlouvaKarta.vyroci")}</div>
                <div className="font-semibold mono" style={{ color: "var(--ink)" }} data-testid={`contract-vyroci-${ct.id}`}>{datum(ct.anniversary_date)}</div>
              </div>
            )}
            {ct.start_date && (
              <div>
                <div className="text-popisek uppercase tracking-wider font-semibold" style={{ color: "var(--muted)" }}>{t("smlouvaKarta.zacatek")}</div>
                <div className="font-semibold mono" style={{ color: "var(--muted)" }} data-testid={`contract-zacatek-${ct.id}`}>{datum(ct.start_date)}</div>
              </div>
            )}
          </div>
          {isInsuranceType(ct.type) && (
            <button
              type="button"
              onClick={vypoved}
              disabled={generating}
              className="mt-3 inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg text-drobny font-semibold transition-all hover:translate-y-[-1px] disabled:opacity-50"
              style={{ background: `${color}14`, color: barvaTextu, outline: `1px solid ${color}33`, outlineOffset: "-1px" }}
              title={t("smlouvaKarta.vypovedTitle", { zakon: t(`smlouvaKarta.zakon.${zeme}`) })}
              data-testid={`contract-vypoved-${ct.id}`}
            >
              <FileText size={12} /> {generating ? t("smlouvaKarta.generuji") : t("smlouvaKarta.vypoved")}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
